/**
 * Professional Call Interface Component for Dashboard
 * Unified component for both voice and video calls with Daily.co iframe
 * Uses the same approach as widget for consistency
 */

import { useEffect, useRef, useState, useCallback } from "react";
import dailyService from "@/lib/services/daily.service";
import type { DailyCall } from "@daily-co/daily-js";

// ============================================================
// Types
// ============================================================
interface CallInterfaceProps {
  roomUrl: string;
  token?: string;
  userName: string;
  userId: string;
  callType: "voice" | "video";
  visitorName?: string;
  visitorAvatar?: string;
  onLeave: () => void;
  onMinimize?: () => void;
}

export const CallInterface = ({
  roomUrl,
  token,
  userName,
  userId,
  callType,
  visitorName = "Visitor",
  visitorAvatar,
  onLeave,
  onMinimize,
}: CallInterfaceProps) => {
  // ============================================================
  // State
  // ============================================================
  const [containerReady, setContainerReady] = useState(false);
  const [callState, setCallState] = useState({
    status: "idle" as "idle" | "joining" | "joined" | "leaving" | "error",
    isMuted: false,
    isVideoOff: false,
    isScreenSharing: false,
    participantCount: 0,
    callDuration: 0,
    error: null as string | null,
    repLeft: false, // Track if sales rep has left the call
  });
  const [startTime, setStartTime] = useState<number | null>(null);
  const [showRetry, setShowRetry] = useState(false);

  // ============================================================
  // Refs
  // ============================================================
  const containerRef = useRef<HTMLDivElement>(null);
  const callFrameRef = useRef<DailyCall | null>(null);
  const hasJoinedRef = useRef(false); // Track if we've already joined to prevent duplicate joins
  const joinedRoomUrlRef = useRef<string | null>(null); // Track which room we've joined
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use callback ref to detect when container is mounted
  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;
      if (element && !containerReady) {
        setContainerReady(true);
      }
    },
    [containerReady]
  );

  // ============================================================
  // Effects
  // ============================================================
  // Initialize call when container is ready
  useEffect(() => {
    if (!containerReady || !containerRef.current) {
      console.log("[CallInterface] Waiting for container...");
      return;
    }

    // Only initialize if we have roomUrl and token
    if (!roomUrl || !token) {
      console.log("[CallInterface] Missing roomUrl or token, staying idle");
      return;
    }

    // If already joined/joining this exact room, don't restart flow unless we are resetting
    // But we need to handle the "remount" case where we need to re-attach listeners
    // For now, let's proceed but check flags inside

    let isMounted = true;
    let durationTimer: ReturnType<typeof setInterval> | null = null;

    // Set status to joining if not already
    setCallState((prev) => {
      if (prev.status === "idle") return { ...prev, status: "joining", error: null };
      return prev;
    });

    // Start 15s connection timeout (increased for slower connections)
    if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
    setShowRetry(false);
    connectionTimeoutRef.current = setTimeout(() => {
      if (isMounted) {
        console.warn("[CallInterface] Connection timed out (15s), showing retry");
        setShowRetry(true);
        // If still joining after timeout, try to check if we're actually joined
        if (callFrameRef.current) {
          try {
            const meetingState = callFrameRef.current.meetingState();
            const participants = callFrameRef.current.participants();
            if (meetingState === "joined-meeting" || Object.keys(participants).length > 0) {
              console.log("[CallInterface] Actually joined despite timeout, updating state");
              setCallState((prev) => ({ ...prev, status: "joined" }));
              setShowRetry(false);
              if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
            }
          } catch (e) {
            console.warn("[CallInterface] Error checking state on timeout:", e);
          }
        }
      }
    }, 15000); // 15 seconds

    // Define event listeners FIRST so they can be used by both paths
    const setupEventListeners = (callFrame: DailyCall) => {
      const updateParticipantCount = (callFrame: DailyCall) => {
        const participants = callFrame.participants();
        const count = Object.keys(participants).length;
        setCallState((prev) => ({ ...prev, participantCount: count }));
      };

      // Joined meeting
      callFrame.on("joined-meeting", () => {
        if (!isMounted) return;
        console.log("[CallInterface] 'joined-meeting' event received!");

        // Clear connection timeout
        if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
        setShowRetry(false);

        setCallState((prev) => ({ ...prev, status: "joined", error: null }));
        setStartTime(Date.now());

        // Check for existing participants immediately
        const participants = callFrame.participants();
        const participantCount = Object.keys(participants).length;

        // Subscribe to all existing remote participants' tracks
        Object.values(participants).forEach((p: any) => {
          if (!p.local) {
            try {
              callFrame.updateParticipant(p.session_id, {
                setSubscribedTracks: { audio: true, video: true },
              });
            } catch (subErr) {
              console.warn(`[CallInterface] Could not subscribe to existing participant:`, subErr);
            }
          }
        });

        setCallState((prev) => ({ ...prev, participantCount }));

        // Also check again after a short delay to catch late joiners
        setTimeout(() => {
          if (isMounted && callFrame) {
            const delayedParticipants = callFrame.participants();
            const delayedCount = Object.keys(delayedParticipants).length;
            if (delayedCount !== participantCount) {
              setCallState((prev) => ({ ...prev, participantCount: delayedCount }));

              // Subscribe to any new participants
              Object.values(delayedParticipants).forEach((p: any) => {
                if (!p.local && !participants[p.session_id]) {
                  try {
                    callFrame.updateParticipant(p.session_id, {
                      setSubscribedTracks: { audio: true, video: true },
                    });
                  } catch (subErr) {
                    console.warn(
                      `[CallInterface] Could not subscribe to late-joining participant:`,
                      subErr
                    );
                  }
                }
              });
            }
          }
        }, 1000);

        // Set up periodic participant check as fallback (every 2 seconds)
        const participantCheckInterval = setInterval(() => {
          if (isMounted && callFrame) {
            const currentParticipants = callFrame.participants();
            const currentCount = Object.keys(currentParticipants).length;
            const remoteParticipants = Object.values(currentParticipants).filter(
              (p: any) => !p.local
            );

            setCallState((prev) => {
              if (prev.participantCount !== currentCount) {
              }
              return { ...prev, participantCount: currentCount };
            });

            // Subscribe to any remote participants we haven't seen before
            remoteParticipants.forEach((p: any) => {
              if (!p.local) {
                // Check if we've already subscribed by checking if tracks are available
                const needsSubscription = !p.audioTrack && !p.videoTrack;
                if (needsSubscription) {
                  try {
                    callFrame.updateParticipant(p.session_id, {
                      setSubscribedTracks: { audio: true, video: true },
                    });
                  } catch (subErr) {
                    console.warn(
                      `[CallInterface] Periodic check: Could not subscribe to participant:`,
                      subErr
                    );
                  }
                }
              }
            });
          } else {
            clearInterval(participantCheckInterval);
          }
        }, 2000);

        // Store interval for cleanup
        (callFrame as any)._participantCheckInterval = participantCheckInterval;
      });

      // Participant joined
      callFrame.on("participant-joined", (event: any) => {
        if (!isMounted) return;
        const participant = event.participant;

        if (!participant.local) {
          // Clear repLeft flag since rep has rejoined
          setCallState((prev) => ({ ...prev, repLeft: false }));

          // Explicitly subscribe to remote participant tracks
          try {
            callFrame.updateParticipant(participant.session_id, {
              setSubscribedTracks: { audio: true, video: true },
            });
          } catch (subErr) {
            console.warn(`[CallInterface] Could not subscribe to tracks:`, subErr);
          }
        }
        updateParticipantCount(callFrame);
      });

      // Participant left
      callFrame.on("participant-left", (event: any) => {
        if (!isMounted) return;
        const participant = event.participant;

        // Check if the sales rep (non-local participant) left
        if (participant && !participant.local) {
          setCallState((prev) => ({ ...prev, repLeft: true }));
        }

        updateParticipantCount(callFrame);
      });

      // Participant updated
      callFrame.on("participant-updated", (event: any) => {
        if (!isMounted) return;
        const participant = event.participant;
        if (participant?.local) {
          setCallState((prev) => ({
            ...prev,
            isMuted: !participant.audio,
            isVideoOff: !participant.video,
            isScreenSharing: participant.screen || false,
          }));
        }
        updateParticipantCount(callFrame);
      });

      // Screen share started
      callFrame.on("screen-share-started" as any, () => {
        if (!isMounted) return;
        setCallState((prev) => ({ ...prev, isScreenSharing: true }));
      });

      // Screen share stopped
      callFrame.on("screen-share-stopped" as any, () => {
        if (!isMounted) return;
        setCallState((prev) => ({ ...prev, isScreenSharing: false }));
      });

      // Error
      callFrame.on("error", (error: any) => {
        if (!isMounted) return;
        console.error("[CallInterface] Daily.co error:", error);
        setCallState((prev) => ({
          ...prev,
          status: "error",
          error: error.errorMsg || error.message || "Call error occurred",
        }));
      });

      // Left meeting
      callFrame.on("left-meeting", () => {
        if (!isMounted) return;
        setCallState((prev) => ({ ...prev, status: "leaving" }));
      });
    };

    // Check for existing call frame (handles remounts where ref is lost)
    const existingCallFrame = dailyService.getCallFrame();
    const isReturningUser = hasJoinedRef.current && joinedRoomUrlRef.current === roomUrl;

    let isFrameActive = false;
    if (existingCallFrame) {
      const state = existingCallFrame.meetingState();
      if (state !== "left-meeting" && state !== "error") {
        // Verify room URL if possible to avoid capturing wrong call
        try {
          const roomInfo = (existingCallFrame as any).room();
          if (roomInfo?.url) {
            // If we have URL, it must match
            if (roomInfo.url === roomUrl) isFrameActive = true;
          } else {
            // If joining, we assume it's the correct call
            isFrameActive = true;
          }
        } catch (e) {
          isFrameActive = true;
        }
      }
    }

    // Reuse frame if we have a local ref match OR an active daily-js frame
    if (isReturningUser || isFrameActive) {
      if (existingCallFrame) {
        callFrameRef.current = existingCallFrame;

        // CRITICAL: Check if iframe needs reparenting (if component remounted)
        try {
          const iframe = (existingCallFrame as any).iframe?.() as HTMLIFrameElement | null;
          if (iframe && containerRef.current && !containerRef.current.contains(iframe)) {
            console.log("[CallInterface] Reparenting existing Daily.co iframe to new container");
            containerRef.current.appendChild(iframe);
          }
        } catch (reparentErr) {
          console.warn("[CallInterface] Error reparenting iframe:", reparentErr);
          // If reparenting fails, the frame is likely corrupted - destroy it
          try {
            dailyService.leaveCall().catch(() => {});
          } catch (e) {}
          callFrameRef.current = null;
          hasJoinedRef.current = false;
          // Fall through to create a new frame
        }

        // CRITICAL: Always setup listeners here so we catch events if we remounted during join
        setupEventListeners(existingCallFrame);

        // Check meeting state and update UI immediately
        // Wrap in try-catch because postMessage may fail if iframe is corrupted
        try {
          const meetingState = existingCallFrame.meetingState();
          const participants = existingCallFrame.participants();

          if (
            meetingState === "joined-meeting" ||
            (participants && Object.keys(participants).length > 0)
          ) {
            console.log("[CallInterface] Already joined! Updating state immediately.");
            if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
            setShowRetry(false);
            setCallState((prev) => ({
              ...prev,
              status: "joined",
              participantCount: Object.keys(participants).length,
            }));
            if (!startTime) setStartTime(Date.now());
          } else if (meetingState === "loading") {
            setCallState((prev) => ({ ...prev, status: "joining" }));
          } else if (meetingState === "joining-meeting") {
            // If stuck in joining-meeting for more than 5 seconds, it's likely stuck
            // Force a rejoin attempt
            console.warn("[CallInterface] Stuck in joining-meeting state, may need to rejoin");
            setCallState((prev) => ({ ...prev, status: "joining" }));
          }
        } catch (err) {
          console.warn(
            "[CallInterface] Error checking existing call frame state (frame may be corrupted):",
            err
          );
          // The frame is likely corrupted - destroy it and allow rejoin
          try {
            dailyService.leaveCall().catch(() => {});
          } catch (e) {}
          callFrameRef.current = null;
          hasJoinedRef.current = false;
          // Don't return - fall through to initializeCall
        }
        if (callFrameRef.current) {
          return; // We have a valid frame, handled. Exit effect.
        }
      }

      // CRITICAL FIX: isReturningUser is true BUT existingCallFrame is null.
      // This means a join is IN PROGRESS from a previous mount.
      // We must NOT return early! We must wait for that join and recapture the result.
      // By calling dailyService.joinCall() again, we get the SAME promise and can await it.
      console.log(
        "[CallInterface] Returning user but no frame yet. Re-calling joinCall to await the in-progress promise."
      );
      // DON'T return here, fall through to initializeCall which will call dailyService.joinCall
      // Reset hasJoinedRef so initializeCall proceeds, but dailyService.joinCall will be idempotent.
      hasJoinedRef.current = false;
    }

    const initializeCall = async () => {
      if (!containerRef.current) {
        console.error("[CallInterface] Container ref not available");
        setCallState((prev) => ({
          ...prev,
          status: "error",
          error: "Call container not found",
        }));
        return;
      }

      // Mark as joining to prevent duplicate calls
      hasJoinedRef.current = true;
      joinedRoomUrlRef.current = roomUrl;

      console.log("[CallInterface] Starting initializeCall for room:", roomUrl);
      try {
        // Request permissions first
        console.log("[CallInterface] Requesting permissions...");
        const constraints: MediaStreamConstraints = {
          audio: true,
          video: callType === "video",
        };

        try {
          // Request permissions with timeout
          const stream = await Promise.race([
            navigator.mediaDevices.getUserMedia(constraints),
            new Promise<MediaStream>((_, reject) =>
              setTimeout(() => reject(new Error("Permission request timed out")), 5000)
            ),
          ]);
          console.log("[CallInterface] Permissions granted/obtained");
          // Stop immediately - Daily.co will request its own stream
          stream.getTracks().forEach((track) => track.stop());
        } catch (permErr) {
          console.warn("[CallInterface] Permission request failed or timed out:", permErr);
          // Continue anyway - Daily.co will handle permissions in iframe
        }

        // Check container again after async operation
        if (!containerRef.current) {
          console.error("[CallInterface] Container ref lost during initialization");
          throw new Error("Call container lost");
        }

        // Join call
        console.log("[CallInterface] Calling dailyService.joinCall...");
        const callFrame = await dailyService.joinCall({
          roomUrl,
          token,
          userName,
          startVideoOff: callType === "voice",
          startAudioOff: false,
          container: containerRef.current,
        });
        console.log("[CallInterface] dailyService.joinCall returned successfully");

        console.log("[CallInterface] Checking isMounted:", isMounted);
        if (!isMounted) {
          console.warn("[CallInterface] Component unmounted during join, aborting");
          return;
        }

        callFrameRef.current = callFrame;
        console.log("[CallInterface] Setting status to 'joined'");

        // Verify we're actually joined before updating state
        try {
          const meetingState = callFrame.meetingState();
          const participants = callFrame.participants();
          console.log(
            "[CallInterface] Meeting state after join:",
            meetingState,
            "Participants:",
            Object.keys(participants).length
          );

          // Only update to joined if we're actually in the meeting
          if (meetingState === "joined-meeting" || Object.keys(participants).length > 0) {
            // Clear timeout on success
            if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
            setShowRetry(false);

            setCallState((prev) => {
              console.log("[CallInterface] Updating call state from:", prev.status, "to joined");
              return { ...prev, status: "joined" };
            });
            setStartTime(Date.now());
          } else {
            console.warn(
              "[CallInterface] Join promise resolved but not actually in meeting, state:",
              meetingState
            );
            // Wait a bit and check again
            setTimeout(() => {
              if (isMounted && callFrame) {
                const retryState = callFrame.meetingState();
                const retryParticipants = callFrame.participants();
                if (retryState === "joined-meeting" || Object.keys(retryParticipants).length > 0) {
                  if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
                  setShowRetry(false);
                  setCallState((prev) => ({ ...prev, status: "joined" }));
                  setStartTime(Date.now());
                }
              }
            }, 1000);
          }
        } catch (verifyErr) {
          console.warn("[CallInterface] Error verifying join state:", verifyErr);
          // Assume joined if we can't verify
          if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
          setShowRetry(false);
          setCallState((prev) => ({ ...prev, status: "joined" }));
          setStartTime(Date.now());
        }

        // Immediately check participants and set up periodic check
        const checkParticipants = () => {
          if (!callFrame) return;
          const participants = callFrame.participants();
          const count = Object.keys(participants).length;
          const remoteParticipants = Object.values(participants).filter((p: any) => !p.local);

          setCallState((prev) => ({ ...prev, participantCount: count }));

          // Subscribe to remote participants
          remoteParticipants.forEach((p: any) => {
            if (!p.local) {
              try {
                callFrame.updateParticipant(p.session_id, {
                  setSubscribedTracks: { audio: true, video: true },
                });
              } catch (subErr) {
                console.warn(`[CallInterface] Could not subscribe:`, subErr);
              }
            }
          });
        };

        // Check immediately
        checkParticipants();

        // Set up periodic check (every 2 seconds)
        const participantCheckInterval = setInterval(() => {
          if (isMounted && callFrame) {
            const currentParticipants = callFrame.participants();
            const currentCount = Object.keys(currentParticipants).length;
            const remoteParticipants = Object.values(currentParticipants).filter(
              (p: any) => !p.local
            );

            setCallState((prev) => {
              if (prev.participantCount !== currentCount) {
              }
              return { ...prev, participantCount: currentCount };
            });

            remoteParticipants.forEach((p: any) => {
              if (!p.local) {
                const needsSubscription = !p.audioTrack && !p.videoTrack;
                if (needsSubscription) {
                  try {
                    callFrame.updateParticipant(p.session_id, {
                      setSubscribedTracks: { audio: true, video: true },
                    });
                  } catch (subErr) {
                    console.warn(`[CallInterface] Periodic: Could not subscribe:`, subErr);
                  }
                }
              }
            });
          } else {
            clearInterval(participantCheckInterval);
          }
        }, 2000);

        // Store interval for cleanup
        (callFrame as any)._participantCheckInterval = participantCheckInterval;

        // Set up event listeners
        setupEventListeners(callFrame);

        // Start duration timer
        durationTimer = setInterval(() => {
          if (startTime) {
            setCallState((prev) => ({
              ...prev,
              callDuration: Math.floor((Date.now() - startTime) / 1000),
            }));
          }
        }, 1000);
      } catch (error: any) {
        console.error("[CallInterface] Error joining call:", error);
        if (isMounted) {
          setCallState((prev) => ({
            ...prev,
            status: "error",
            error: error.message || "Failed to join call",
          }));
        }
      }
    };

    // Setup listeners call is already in initializeCall, no need to duplicate definition

    initializeCall();

    return () => {
      isMounted = false;
      if (durationTimer) {
        clearInterval(durationTimer);
      }
      // Cleanup is handled by dailyService
      if (callFrameRef.current && joinedRoomUrlRef.current !== roomUrl) {
        dailyService.leaveCall().catch(console.error);
      }
    };
  }, [containerReady, roomUrl, token, userName, userId, callType]);

  // Update duration when startTime changes
  useEffect(() => {
    if (!startTime) return;

    const timer = setInterval(() => {
      setCallState((prev) => ({
        ...prev,
        callDuration: Math.floor((Date.now() - startTime) / 1000),
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  // ============================================================
  // Handlers
  // ============================================================
  const handleLeave = async () => {
    setCallState((prev) => ({ ...prev, status: "leaving" }));
    try {
      await dailyService.leaveCall();
    } catch (error) {
      console.error("[CallInterface] Error leaving call:", error);
    } finally {
      onLeave();
    }
  };

  const handleToggleMute = async () => {
    try {
      await dailyService.toggleMicrophone();
      const callFrame = dailyService.getCallFrame();
      if (callFrame) {
        const localParticipant = callFrame.participants().local;
        setCallState((prev) => ({
          ...prev,
          isMuted: !localParticipant?.audio,
        }));
      }
    } catch (error) {
      console.error("[CallInterface] Error toggling mute:", error);
    }
  };

  const handleToggleVideo = async () => {
    if (callType !== "video") return;

    try {
      await dailyService.toggleCamera();
      const callFrame = dailyService.getCallFrame();
      if (callFrame) {
        const localParticipant = callFrame.participants().local;
        setCallState((prev) => ({
          ...prev,
          isVideoOff: !localParticipant?.video,
        }));
      }
    } catch (error) {
      console.error("[CallInterface] Error toggling video:", error);
    }
  };

  const handleToggleScreenShare = async () => {
    try {
      if (callState.isScreenSharing) {
        await dailyService.stopScreenShare();
      } else {
        await dailyService.startScreenShare();
      }
    } catch (error) {
      console.error("[CallInterface] Error toggling screen share:", error);
    }
  };

  // ============================================================
  // Helpers
  // ============================================================
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // ============================================================
  // Renderers
  // ============================================================
  return (
    <div
      className={`fixed bottom-5 right-5 w-[400px] bg-white rounded-2xl shadow-2xl overflow-hidden z-10000 flex flex-col 
        ${callType === "video" ? "h-[500px]" : "h-[350px]"}`}
    >
      {/* Daily.co iframe container - handles all video/audio rendering */}
      <div
        ref={setContainerRef}
        className={`flex-1 relative ${
          callType === "voice" ? "absolute top-0 left-0 w-[200px] h-[200px] overflow-hidden" : ""
        } ${callState.status === "joining" ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        style={{
          minHeight: callType === "video" ? "400px" : callType === "voice" ? "200px" : "0",
          zIndex: callType === "voice" ? -1 : 0,
          transform: callType === "voice" ? "translateX(-9999px)" : "none",
        }}
      />

      {/* Loading State Overlay */}
      {callState.status === "joining" && (
        <div className="absolute inset-0 bg-linear-to-br from-pri to-sec flex items-center justify-center z-50">
          <div className="text-center text-white">
            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm font-medium mb-4">Connecting to {visitorName}...</p>

            {showRetry && (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer border border-white/40"
              >
                Connection taking too long? Click to Refresh
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error State Overlay */}
      {callState.error && (
        <div className="absolute inset-0 bg-white flex items-center justify-center z-50">
          <div className="text-center p-5 text-gray-800">
            <h3 className="text-lg font-semibold mb-3 text-red-600">Call Error</h3>
            <p className="text-sm text-gray-600 mb-5">{callState.error}</p>
            <button
              onClick={handleLeave}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-semibold cursor-pointer transition-colors hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Voice Interface - Show when callType is voice and joined */}
      {callType === "voice" && callState.status === "joined" && !callState.error && (
        <div className="flex flex-col items-center justify-center p-5 gap-2 bg-linear-to-br from-pri to-sec h-full absolute inset-0 mb-[80px]">
          <div className="size-[90px] rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-4 border-white/30">
            {visitorAvatar ? (
              <img src={visitorAvatar} alt={visitorName} className="w-full h-full object-cover" />
            ) : (
              <div className="text-5xl font-semibold text-white">
                {visitorName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()}
              </div>
            )}
          </div>
          <h3 className="text-2xl font-semibold text-white">
            {callState.participantCount > 1 ? visitorName : "Are you ready to join?"}
          </h3>
          <p className="text-sm text-white/90">
            {callState.participantCount > 1 ? "Connected" : `Waiting for ${visitorName} to join...`}
          </p>
          {callState.participantCount === 1 && (
            <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin mt-2"></div>
          )}
        </div>
      )}

      {/* Call Controls - Show when not joining and no error */}
      {callState.status !== "joining" && !callState.error && (
        <div className="bg-white p-4 flex flex-col gap-3 border-t border-gray-200 z-10 relative mt-auto">
          <div className="flex justify-between items-center text-xs text-gray-600">
            <span className="font-semibold text-gray-800">
              {formatDuration(callState.callDuration)}
            </span>
            <span>
              {callState.participantCount}{" "}
              {callState.participantCount === 1 ? "participant" : "participants"}
            </span>
          </div>

          <div className="flex justify-center gap-3">
            {/* Mute Toggle */}
            <button
              className={`w-12 h-12 rounded-full border-none cursor-pointer flex items-center justify-center transition-all relative ${
                callState.isMuted
                  ? "bg-red-100 text-red-600 hover:bg-red-200"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              } hover:scale-105 active:scale-95`}
              onClick={handleToggleMute}
              title={callState.isMuted ? "Unmute" : "Mute"}
            >
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {callState.isMuted ? (
                  <>
                    <path d="M12 8v8M8 10v4M16 10v4" />
                    <path d="M3 3l18 18" />
                  </>
                ) : (
                  <>
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <path d="M12 18v4M8 22h8" />
                  </>
                )}
              </svg>
            </button>

            {/* Video Toggle (only for video calls) */}
            {callType === "video" && (
              <button
                className={`w-12 h-12 rounded-full border-none cursor-pointer flex items-center justify-center transition-all relative ${
                  callState.isVideoOff
                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                } hover:scale-105 active:scale-95`}
                onClick={handleToggleVideo}
                title={callState.isVideoOff ? "Turn on camera" : "Turn off camera"}
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  {callState.isVideoOff ? (
                    <>
                      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                      <path d="M1 1l22 22" />
                    </>
                  ) : (
                    <>
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </>
                  )}
                </svg>
              </button>
            )}

            {/* Screen Share (only for video calls) */}
            {callType === "video" && (
              <button
                className={`w-12 h-12 rounded-full border-none cursor-pointer flex items-center justify-center transition-all relative ${
                  callState.isScreenSharing
                    ? "bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                } hover:scale-105 active:scale-95`}
                onClick={handleToggleScreenShare}
                title={callState.isScreenSharing ? "Stop sharing" : "Share screen"}
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <path d="M8 21h8M12 17v4" />
                </svg>
              </button>
            )}

            {/* Minimize (if provided) */}
            {onMinimize && (
              <button
                className="w-12 h-12 rounded-full border-none cursor-pointer flex items-center justify-center transition-all relative bg-gray-100 text-gray-800 hover:bg-gray-200 hover:scale-105 active:scale-95"
                onClick={onMinimize}
                title="Minimize"
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              </button>
            )}

            {/* Leave Call */}
            <button
              className="w-12 h-12 rounded-full border-none cursor-pointer flex items-center justify-center transition-all relative bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95"
              onClick={handleLeave}
              title="Leave call"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M16 10l-6-6-6 6M10 18v-8"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Waiting Overlay - Show when sales rep has left */}
      {callState.repLeft && (
        <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-sm flex flex-col items-center justify-center z-50 p-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-bold text-white">{visitorName} has left the call</h3>
              <p className="text-sm text-slate-300">They may be reconnecting. Please wait...</p>
            </div>
            <button
              onClick={onLeave}
              className="mt-4 px-6 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg font-medium transition-colors"
            >
              End Call
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
